/**
 * `useEntityNeighbors` — thin React Query hook around
 * `api.graph.getNodeNeighbors(caseId, nodeId)`.
 *
 * Per design D7a: 1-hop only, no `hops` argument. The hook fires whenever it
 * runs with a populated `caseId` and `nodeId`; because Radix lazily mounts
 * inactive `TabsContent`, the Neighbours tab only mounts when the user clicks
 * it — which gives us lazy loading for free without a manual `active` gate.
 * React Query key `['graph', caseId, 'neighbors', nodeId]` so re-opening the
 * same node on a different view reads from cache with no new request.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '@/services/api'
import type { Person, Relationship } from '@/services/api/graph'

/** Cache key for a node's 1-hop neighbours — shared with eviction sites. */
export const entityNeighborsQueryKey = (caseId: string, nodeId: string | null) =>
  ['graph', caseId, 'neighbors', nodeId] as const

interface UseEntityNeighborsArgs {
  caseId: string
  nodeId: string | null
}

export interface NeighborsResponse {
  nodes: Person[]
  relationships: Relationship[]
}

interface UseEntityNeighborsResult {
  data: NeighborsResponse | null
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
}

export function useEntityNeighbors({
  caseId,
  nodeId,
}: UseEntityNeighborsArgs): UseEntityNeighborsResult {
  const enabled = Boolean(nodeId && caseId)

  const query = useQuery<NeighborsResponse>({
    queryKey: entityNeighborsQueryKey(caseId, nodeId),
    queryFn: () => api.graph.getNodeNeighbors(caseId, nodeId as string),
    enabled,
    staleTime: 30_000,
  })

  return {
    data: query.data ?? null,
    isLoading: enabled && query.isLoading,
    isFetching: query.isFetching,
    error: (query.error as Error | null) ?? null,
    refetch: () => {
      void query.refetch()
    },
  }
}
